'use client'

import { Calendar as CalendarIcon, Check } from 'lucide-react'
import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '../cn'

export type TaskBoardCardTone = 'default' | 'muted' | 'danger' | 'warning' | 'success'
export type TaskBoardCardCompletionDisplay = 'task' | 'live'

export interface TaskBoardCardPill {
  id: string
  label: string
  tone?: TaskBoardCardTone
}

export interface TaskBoardCardCount {
  id: string
  icon: ReactNode
  count: number
}

export interface TaskBoardCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: string
  completed?: boolean
  completionDisplay?: TaskBoardCardCompletionDisplay
  completionDisabled?: boolean
  completionPending?: boolean
  selected?: boolean
  multiSelected?: boolean
  isDragging?: boolean
  assigneeLabel?: string | null
  assigneeImage?: string | null
  dueLabel?: string | null
  dueTone?: TaskBoardCardTone
  pills?: TaskBoardCardPill[]
  counts?: TaskBoardCardCount[]
  onSelect?: () => void
  onComplete?: () => void
}

export function TaskBoardCard({
  title,
  completed = false,
  completionDisplay = 'task',
  completionDisabled = false,
  completionPending = false,
  selected = false,
  multiSelected = false,
  isDragging = false,
  assigneeLabel,
  assigneeImage,
  dueLabel,
  dueTone = 'muted',
  pills = [],
  counts = [],
  onSelect,
  onComplete,
  className,
  ...props
}: TaskBoardCardProps) {
  const canComplete = Boolean(onComplete) && !completionDisabled
  const interactive = Boolean(onSelect)
  const completedAsTask = completed && completionDisplay === 'task'
  const visibleCounts = counts.filter((item) => item.count > 0)

  return (
    <div
      data-dragging={isDragging ? 'true' : undefined}
      data-selected={selected ? 'true' : undefined}
      className={cn(
        'group relative min-h-[76px] max-h-[320px] overflow-hidden rounded-lg border border-border/20 bg-card-elevated p-3 text-sm shadow-sm transition-all md:border-border/30',
        interactive && 'cursor-pointer md:hover:border-muted-foreground/50 md:hover:shadow-md',
        selected && !multiSelected && 'md:border-primary md:ring-1 md:ring-primary/20',
        multiSelected && 'border-primary bg-primary/5 ring-1 ring-primary/20',
        completedAsTask && 'opacity-85',
        isDragging && 'scale-[1.01] shadow-lg',
        className,
      )}
      onClick={() => onSelect?.()}
      {...props}>
      <div className="text-sm leading-6">
        <button
          type="button"
          aria-label={`Complete ${title}`}
          disabled={!canComplete}
          onClick={(event) => {
            event.stopPropagation()
            onComplete?.()
          }}
          className={cn(
            'float-left mr-2 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
            completed ? 'border-transparent bg-green-700/60 text-white' : 'border-muted-foreground/20',
            canComplete && !completed && 'hover:border-green-600/60',
            !canComplete && 'cursor-default',
          )}>
          {(completed || completionPending) && <Check className="h-3 w-3" />}
        </button>
        <span className={cn('break-words', completedAsTask && 'text-muted-foreground line-through')}>{title}</span>
      </div>

      {(pills.length > 0 || assigneeLabel || dueLabel || visibleCounts.length > 0) && (
        <div className="relative mt-3 space-y-3">
          {pills.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1">
              {pills.map((pill) => (
                <TaskBoardCardPillView key={pill.id} pill={pill} />
              ))}
            </div>
          ) : null}

          {(assigneeLabel || dueLabel || visibleCounts.length > 0) && (
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2 text-xs">
                {assigneeLabel ? <TaskBoardCardAssignee label={assigneeLabel} image={assigneeImage} /> : null}
                {dueLabel ? (
                  <span className={cn('inline-flex min-w-0 items-center gap-1', toneClassName(dueTone))}>
                    <CalendarIcon className="h-3 w-3 shrink-0" />
                    <span className="truncate">{dueLabel}</span>
                  </span>
                ) : null}
              </div>

              {visibleCounts.length > 0 ? (
                <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                  {visibleCounts.map((item) => (
                    <span key={item.id} className="flex items-center gap-0.5">
                      {item.icon}
                      {item.count}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-b from-transparent to-[var(--card-elevated,theme(colors.background))] opacity-0 [.overflow-hidden:has(>*:last-child):has(&)]:opacity-100"
      />
    </div>
  )
}

function TaskBoardCardPillView({ pill }: { pill: TaskBoardCardPill }) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center rounded-md px-2 py-0.5 text-xs font-medium',
        pillClassName(pill.tone),
      )}>
      <span className="truncate">{pill.label}</span>
    </span>
  )
}

function TaskBoardCardAssignee({ label, image }: { label: string; image?: string | null }) {
  if (image) {
    return (
      <img
        src={image}
        alt={label}
        title={label}
        className="h-5 w-5 shrink-0 rounded-full border border-border object-cover"
      />
    )
  }

  return (
    <span
      title={label}
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-[10px] font-semibold text-muted-foreground">
      {assigneeInitials(label)}
    </span>
  )
}

function assigneeInitials(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) {
    return '?'
  }
  if (words.length === 1) {
    return words[0]!.slice(0, 2).toUpperCase()
  }
  return `${words[0]![0]}${words[words.length - 1]![0]}`.toUpperCase()
}

function pillClassName(tone: TaskBoardCardTone = 'muted'): string {
  switch (tone) {
    case 'danger':
      return 'bg-red-500/15 text-red-700 dark:text-red-300'
    case 'warning':
      return 'bg-yellow-500/15 text-yellow-800 dark:text-yellow-300'
    case 'success':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    case 'default':
      return 'bg-blue-500/15 text-blue-700 dark:text-blue-300'
    case 'muted':
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function toneClassName(tone: TaskBoardCardTone = 'muted'): string {
  switch (tone) {
    case 'danger':
      return 'text-destructive'
    case 'warning':
      return 'text-amber-700 dark:text-amber-400'
    case 'success':
      return 'text-emerald-700 dark:text-emerald-400'
    case 'default':
      return 'text-foreground'
    case 'muted':
    default:
      return 'text-muted-foreground'
  }
}
